// actions/reportAction.js
import axios from 'axios'
import {
  GET_REPORTS_REQUEST,
  GET_REPORTS_SUCCESS,
  GET_REPORTS_FAIL,
  GET_REPORTS_RESET,
  GET_DETAILED_TRANSACTIONS_REQUEST,
  GET_DETAILED_TRANSACTIONS_SUCCESS,
  GET_DETAILED_TRANSACTIONS_FAIL,
  GET_DETAILED_TRANSACTIONS_RESET,
  SET_REPORT_FILTER_REQUEST,
  SET_REPORT_FILTER_SUCCESS,
  SET_REPORT_FILTER_FAIL,
  SET_REPORT_FILTER_RESET,
  EXPORT_REPORTS_REQUEST,
  EXPORT_REPORTS_SUCCESS,
  EXPORT_REPORTS_FAIL,
  EXPORT_REPORTS_RESET,
  GET_AVAILABLE_CLUBS_REQUEST,
  GET_AVAILABLE_CLUBS_SUCCESS,
  GET_AVAILABLE_CLUBS_FAIL,
  SET_SELECTED_CLUB,
  CLEAR_SELECTED_CLUB,
  SET_SELECTED_CLUBS,
  CLEAR_SELECTED_CLUBS,
} from '../constants/reportConstants'

const baseUrl = process.env.REACT_APP_API_URL

// AbortController for cancelling stale requests
let abortController = null

// Get Reports with filters (supports both single and multi-club selection)
export const getReports = (filter = 'today', customStartDate = null, customEndDate = null, clubIds = null) => async (dispatch, getState) => {
  try {
    // Cancel previous request if still pending
    if (abortController) {
      abortController.abort()
    }

    abortController = new AbortController()

    dispatch({
      type: GET_REPORTS_REQUEST,
    })

    const {
      adminLogin: { userInfo: adminInfo },
      selectedClub: { clubId },  // Legacy single club
      selectedClubs,              // New multi-club state
    } = getState()

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminInfo.token}`,
      },
      signal: abortController.signal,  // Add abort signal
    }

    let url = `${baseUrl}/reports?filter=${filter}`
    if (filter === 'custom' && customStartDate && customEndDate) {
      url += `&customStartDate=${customStartDate}&customEndDate=${customEndDate}`
    }

    // Priority: clubIds parameter > Redux selectedClubs > Legacy clubId
    if (clubIds && clubIds.length > 0) {
      // Multi-club: passed as parameter (from Reports.js)
      url += `&clubIds=${clubIds.join(',')}`
    } else if (selectedClubs?.clubIds && selectedClubs.clubIds.length > 0) {
      // Multi-club: from Redux state
      url += `&clubIds=${selectedClubs.clubIds.join(',')}`
    } else if (clubId) {
      // Legacy single club filter (backward compatibility)
      url += `&clubId=${clubId}`
    }

    const { data } = await axios.get(url, config)

    dispatch({
      type: GET_REPORTS_SUCCESS,
      payload: data,
    })
  } catch (error) {
    // Ignore cancelled requests
    if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
      console.log('[Reports] Previous request cancelled')
      return
    }

    dispatch({
      type: GET_REPORTS_FAIL,
      payload:
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message || 'Failed to fetch reports',
    })
  }
}

// Get Detailed Transactions
export const getDetailedTransactions = (
  filter = 'today',
  customStartDate = null,
  customEndDate = null,
  page = 1,
  limit = 10,
  transactionType = 'all',
  clubIds = null
) => async (dispatch, getState) => {
  try {
    dispatch({
      type: GET_DETAILED_TRANSACTIONS_REQUEST,
    })

    const {
      adminLogin: { userInfo: adminInfo },
      selectedClub: { clubId },
      selectedClubs,
    } = getState()

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminInfo.token}`,
      },
    }

    let url = `${baseUrl}/reports/detailed?filter=${filter}&page=${page}&limit=${limit}&transactionType=${transactionType}`
    if (filter === 'custom' && customStartDate && customEndDate) {
      url += `&customStartDate=${customStartDate}&customEndDate=${customEndDate}`
    }

    // Priority: explicit clubIds argument > Redux selectedClubs > legacy clubId
    if (clubIds && clubIds.length > 0) {
      url += `&clubIds=${clubIds.join(',')}`
    } else if (selectedClubs?.clubIds && selectedClubs.clubIds.length > 0) {
      url += `&clubIds=${selectedClubs.clubIds.join(',')}`
    } else if (clubId) {
      url += `&clubId=${clubId}`
    }

    const { data } = await axios.get(url, config)

    dispatch({
      type: GET_DETAILED_TRANSACTIONS_SUCCESS,
      payload: data,
    })
  } catch (error) {
    dispatch({
      type: GET_DETAILED_TRANSACTIONS_FAIL,
      payload:
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message || 'Failed to fetch detailed transactions',
    })
  }
}

// Set Report Filter (for local state management)
export const setReportFilter = (filterData) => async (dispatch) => {
  try {
    dispatch({
      type: SET_REPORT_FILTER_REQUEST,
    })

    dispatch({
      type: SET_REPORT_FILTER_SUCCESS,
      payload: filterData,
    })
  } catch (error) {
    dispatch({
      type: SET_REPORT_FILTER_FAIL,
      payload: 'Failed to set filter',
    })
  }
}

// Export Reports
export const exportReports = (
  filter = 'today',
  customStartDate = null,
  customEndDate = null,
  format = 'excel'
) => async (dispatch, getState) => {
  try {
    dispatch({
      type: EXPORT_REPORTS_REQUEST,
    })

    const {
      adminLogin: { userInfo: adminInfo },
      selectedClub: { clubId },
      selectedClubs,  // Multi-club support
    } = getState()

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminInfo.token}`,
      },
      responseType: 'blob', // Important for file downloads
    }

    // Use correct backend endpoint
    let url = `${baseUrl}/reports/export/${format}?filter=${filter}`
    if (filter === 'custom' && customStartDate && customEndDate) {
      url += `&customStartDate=${customStartDate}&customEndDate=${customEndDate}`
    }

    // Priority: Multi-club selection > Legacy single club
    if (selectedClubs?.clubIds && selectedClubs.clubIds.length > 0) {
      url += `&clubIds=${selectedClubs.clubIds.join(',')}`
    } else if (clubId) {
      url += `&clubId=${clubId}`
    }

    const { data } = await axios.get(url, config)

    // Create download link
    const fileExtension = format === 'pdf' ? 'pdf' : 'xlsx'
    const downloadUrl = window.URL.createObjectURL(new Blob([data]))
    const link = document.createElement('a')
    link.href = downloadUrl
    link.setAttribute('download', `reports_${filter}_${new Date().toISOString().split('T')[0]}.${fileExtension}`)
    document.body.appendChild(link)
    link.click()
    link.remove()

    dispatch({
      type: EXPORT_REPORTS_SUCCESS,
      payload: 'Reports exported successfully',
    })
  } catch (error) {
    dispatch({
      type: EXPORT_REPORTS_FAIL,
      payload:
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message || 'Failed to export reports',
    })
  }
}

// Get Available Clubs for Reports
export const getAvailableClubs = () => async (dispatch, getState) => {
  try {
    dispatch({
      type: GET_AVAILABLE_CLUBS_REQUEST,
    })

    const {
      adminLogin: { userInfo: adminInfo },
    } = getState()

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminInfo.token}`,
      },
    }

    const { data } = await axios.get(`${baseUrl}/reports/clubs`, config)

    dispatch({
      type: GET_AVAILABLE_CLUBS_SUCCESS,
      payload: data,
    })
  } catch (error) {
    dispatch({
      type: GET_AVAILABLE_CLUBS_FAIL,
      payload:
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message || 'Failed to fetch available clubs',
    })
  }
}

// Set Selected Club for Reports
export const setSelectedClub = (clubId, clubName = null) => (dispatch) => {
  dispatch({
    type: SET_SELECTED_CLUB,
    payload: { clubId, clubName },
  })
}

// Clear Selected Club
export const clearSelectedClub = () => (dispatch) => {
  dispatch({
    type: CLEAR_SELECTED_CLUB,
  })
}

// Set Selected Clubs for Reports (Multi-Club)
export const setSelectedClubs = (clubIds, clubNames = []) => (dispatch) => {
  dispatch({
    type: SET_SELECTED_CLUBS,
    payload: { clubIds, clubNames },
  })
}

// Clear Selected Clubs
export const clearSelectedClubs = () => (dispatch) => {
  dispatch({
    type: CLEAR_SELECTED_CLUBS,
  })
}
